/**
 * Client-side radar value computation — mirrors backend radar_profile.py formulas.
 * Used on ProfileReviewPage (no saved profile_id needed).
 */

function clamp(v: number, min = 1, max = 10): number {
  return Math.min(max, Math.max(min, v));
}

/** Linear interpolation over a set of [x, y] anchors. */
function interpolate(val: number, anchors: [number, number][]): number {
  if (anchors.length === 0) return 5;
  if (val <= anchors[0][0]) return anchors[0][1];
  if (val >= anchors[anchors.length - 1][0]) return anchors[anchors.length - 1][1];
  for (let i = 0; i < anchors.length - 1; i++) {
    const [x0, y0] = anchors[i];
    const [x1, y1] = anchors[i + 1];
    if (val >= x0 && val <= x1) {
      const t = (val - x0) / (x1 - x0);
      return y0 + t * (y1 - y0);
    }
  }
  return anchors[anchors.length - 1][1];
}

export function computeRadarFromFormData(formData: any): {
  head: Record<string, number>;
  shaft: Record<string, number>;
} {
  // Handle nested formData or flat
  const swing = formData.driver_swing_data || formData;
  const basic = formData.basic_info || formData;
  const ballFlight = formData.ball_flight_data || formData;

  const swingSpeed: number = parseFloat(swing.swing_speed_driver) || 95;
  const handicap: number = parseFloat(basic.handicap) || 15;
  const attackAngle: number = parseFloat(swing.attack_angle_driver) || 0;
  const spinRate: number = parseFloat(ballFlight.spin_rate_driver) || 2500;
  const launchAngle: number | null = ballFlight.launch_angle_driver
    ? parseFloat(ballFlight.launch_angle_driver)
    : null;
  const faceToPath: number = parseFloat(swing.face_to_path_driver) || 0;
  const age: number = parseFloat(basic.age) || 35;

  const optimalSpin = interpolate(swingSpeed, [
    [75, 3150], [85, 2750], [95, 2550], [105, 2350], [115, 2150],
  ]);

  // --- Head axes ---
  const forgiveness = clamp(2.0 + (handicap / 30) * 7.5);

  let launch: number;
  if (launchAngle !== null) {
    const optimalLaunch = interpolate(swingSpeed, [
      [75, 16.5], [85, 14.5], [95, 13.5], [105, 12.5], [115, 11.5],
    ]);
    launch = clamp(5.0 + (optimalLaunch - launchAngle) * 0.8);
  } else {
    launch = clamp(5.0 - attackAngle * 0.6);
  }

  const spin = clamp(5.0 + (optimalSpin - spinRate) / 300);
  const workability = clamp(10.0 - (handicap / 30) * 7.5);
  const technology = 7.0;

  // --- Shaft axes ---
  const stiffnessFit = clamp(interpolate(swingSpeed, [
    [65, 1.0], [75, 3.0], [85, 4.5], [95, 6.0], [105, 8.0], [115, 9.5],
  ]));

  const weightBase = interpolate(swingSpeed, [
    [70, 3.0], [85, 5.0], [95, 6.0], [105, 7.5], [115, 9.0],
  ]);
  const ageAdj = interpolate(age, [
    [25, 0], [45, 0], [55, -0.5], [65, -1.5], [75, -2.5],
  ]);
  const weightFit = clamp(weightBase + ageAdj);

  const speedFactor = interpolate(swingSpeed, [[75, 2.0], [95, 0], [115, -2.0]]);
  const aoaFactor = attackAngle * 0.4;
  const launchProfile = clamp(5.0 - speedFactor - aoaFactor);

  const spinProfile = clamp(5.0 + (optimalSpin - spinRate) / 250);
  const stability = clamp(5.0 + Math.abs(faceToPath) * 0.5);

  return {
    head: { forgiveness, launch, spin, workability, technology },
    shaft: { stiffness_fit: stiffnessFit, weight_fit: weightFit, launch_profile: launchProfile, spin_profile: spinProfile, stability },
  };
}
